import { Head, router } from '@inertiajs/react'
import { useState } from 'react'
import AdminLayout from '../../components/layout/AdminLayout'
import Button from '../../components/ui/Button'
import PartnerCard from '../../components/collaboration/PartnerCard'
import AddPartnerModal from '../../components/collaboration/AddPartnerModal'
import PartnerRulesModal from '../../components/collaboration/PartnerRulesModal'
import PartnerStatsModal from '../../components/collaboration/PartnerStatsModal'

export default function CollaborationPartners({ trusted, blocked, pending, available }) {
  const [addModalOpen, setAddModalOpen] = useState(false)
  const [rulesModalOpen, setRulesModalOpen] = useState(false)
  const [statsModalOpen, setStatsModalOpen] = useState(false)
  const [selectedPartner, setSelectedPartner] = useState(null)

  // Calculate stats
  const stats = {
    trusted_count: trusted?.length || 0,
    blocked_count: blocked?.length || 0,
    pending_count: pending?.length || 0,
    total_matches: trusted?.reduce((sum, p) => sum + (p.partnership?.total_matches || 0), 0) || 0,
    success_rate: 0
  }

  // Calculate success rate
  const totalMatches = stats.total_matches
  const successfulMatches = trusted?.reduce((sum, p) => sum + (p.partnership?.successful_matches || 0), 0) || 0
  stats.success_rate = totalMatches > 0 ? ((successfulMatches / totalMatches) * 100).toFixed(1) : 0

  const openRulesModal = (partner) => {
    setSelectedPartner(partner)
    setRulesModalOpen(true)
  }

  const openStatsModal = (partner) => {
    setSelectedPartner(partner)
    setStatsModalOpen(true)
  }

  const handleRemovePartner = (partnerId) => {
    if (confirm('Êtes-vous sûr de vouloir retirer ce partenaire ?')) {
      router.delete(`/collaboration/partners/${partnerId}`)
    }
  }

  const handleBlockAgency = (agencyId) => {
    if (confirm('Êtes-vous sûr de vouloir bloquer cette agence ?')) {
      router.post(`/collaboration/block/${agencyId}`)
    }
  }

  const handleUnblockAgency = (agencyId) => {
    router.delete(`/collaboration/block/${agencyId}`)
  }

  return (
    <AdminLayout>
      <Head title="Gestion des partenaires" />

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Gestion des partenaires</h1>
            <p className="text-gray-600 mt-1">
              Gérez vos relations avec les autres agences du réseau
            </p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" href="/collaboration/settings">
              ⚙️ Paramètres
            </Button>
            <Button onClick={() => setAddModalOpen(true)}>
              + Ajouter un partenaire
            </Button>
          </div>
        </div>

        {/* Stats globales */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-2xl font-bold text-green-600">{stats.trusted_count}</div>
            <div className="text-sm text-gray-600">Partenaires de confiance</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-2xl font-bold text-blue-600">{stats.total_matches}</div>
            <div className="text-sm text-gray-600">Matches réseau</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-2xl font-bold text-purple-600">{stats.success_rate}%</div>
            <div className="text-sm text-gray-600">Taux de succès</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-2xl font-bold text-red-600">{stats.blocked_count}</div>
            <div className="text-sm text-gray-600">Agences bloquées</div>
          </div>
        </div>

        {/* Partenaires de confiance */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Partenaires de confiance ({stats.trusted_count})
            </h2>
          </div>
          <div className="p-6">
            {stats.trusted_count === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Aucun partenaire de confiance. Ajoutez-en un pour commencer à collaborer.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {trusted.map(partner => (
                  <PartnerCard
                    key={partner.id}
                    partner={partner}
                    onViewStats={() => openStatsModal(partner)}
                    onEditRules={() => openRulesModal(partner)}
                    onRemove={() => handleRemovePartner(partner.id)}
                    onBlock={() => handleBlockAgency(partner.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Demandes en attente */}
        {pending && pending.length > 0 && (
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Demandes en attente ({pending.length})
              </h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {pending.map(partner => (
                  <div key={partner.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div>
                      <h3 className="font-medium text-gray-900">{partner.target_agency?.name || partner.name}</h3>
                      <p className="text-sm text-gray-600">{partner.target_agency?.city || partner.city}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => router.post(`/collaboration/partners/${partner.target_agency_id || partner.id}/approve`)}>
                        Approuver
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => router.delete(`/collaboration/partners/${partner.target_agency_id || partner.id}`)}>
                        Refuser
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Agences bloquées */}
        {blocked && blocked.length > 0 && (
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Agences bloquées ({blocked.length})
              </h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {blocked.map(agency => (
                  <div key={agency.id} className="flex items-center justify-between p-4 border border-red-200 bg-red-50 rounded-lg">
                    <div>
                      <h3 className="font-medium text-gray-900">{agency.name}</h3>
                      <p className="text-sm text-gray-600">{agency.city}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleUnblockAgency(agency.id)}
                    >
                      Débloquer
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modales */}
      <AddPartnerModal
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
      />

      {selectedPartner && (
        <>
          <PartnerRulesModal
            isOpen={rulesModalOpen}
            onClose={() => {
              setRulesModalOpen(false)
              setSelectedPartner(null)
            }}
            partner={selectedPartner}
          />

          <PartnerStatsModal
            isOpen={statsModalOpen}
            onClose={() => {
              setStatsModalOpen(false)
              setSelectedPartner(null)
            }}
            partner={selectedPartner}
          />
        </>
      )}
    </AdminLayout>
  )
}
